import React from 'react';
import { Box, Container, Typography, Button } from '@mui/material';
import { FaExclamationTriangle, FaRedo, FaHome } from 'react-icons/fa';

// Top-level error boundary. Wraps the routed <Outlet> INSIDE the Layout so a
// render/data throw in any page keeps the navbar + footer and shows a branded
// fallback instead of a blank white screen or React Router's default error UI.
//
// Complements lazyWithReload in App.js: that handles code-split chunk 404s
// (stale-deploy self-heal); this catches everything else a page can throw at
// render time. ChunkLoadErrors that survive the reload guard land here too.
//
// `resetKey` (the current pathname) lets the boundary recover on navigation:
// when it changes, the error state clears so a fresh route renders normally
// rather than inheriting the previous page's error.
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error('[error-boundary] caught render error:', error, info);
    }
    // Single hook point for future telemetry (Sentry beacon, etc.).
  }

  componentDidUpdate(prevProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <Container maxWidth="sm" sx={{ py: { xs: 8, md: 12 }, textAlign: 'center' }}>
        <Box
          aria-hidden
          sx={{ display: 'flex', justifyContent: 'center', color: 'primary.main', fontSize: 40, mb: 3 }}
        >
          <FaExclamationTriangle />
        </Box>
        <Typography
          component="h1"
          sx={{ fontWeight: 700, fontSize: { xs: '1.75rem', md: '2.25rem' }, letterSpacing: '-0.03em', mb: 2, color: 'text.primary' }}
        >
          Something went wrong
        </Typography>
        <Typography sx={{ color: 'text.secondary', fontSize: '1.05rem', lineHeight: 1.7, maxWidth: '46ch', mx: 'auto', mb: 4 }}>
          This page hit an unexpected error. Reloading usually fixes it — if it
          keeps happening, head back home and try another route.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            onClick={this.handleReload}
            variant="contained"
            startIcon={<FaRedo size={13} />}
            sx={{ borderRadius: 2, fontWeight: 600, px: 3, '&:active': { transform: 'scale(0.97)' } }}
          >
            Reload page
          </Button>
          <Button
            href="/"
            variant="outlined"
            startIcon={<FaHome size={13} />}
            sx={{ borderRadius: 2, fontWeight: 600, px: 3, '&:active': { transform: 'scale(0.97)' } }}
          >
            Back to home
          </Button>
        </Box>
      </Container>
    );
  }
}

export default ErrorBoundary;
